/* ===================================================
   imageUtils.js — Pure functions for pixel manipulation
   
   Blend modes, adjustments, color math, and preview
   dimension helpers. No React dependencies.
   =================================================== */

/* ---- Color space conversion ---- */

export function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6;
    }
  }
  return [h * 360, s, l];
}

export function hslToRgb(h, s, l) {
  h /= 360;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255)
  ];
}

/* ---- Blend mode functions ---- */

const blendLinearDodge = (base, over, op) => Math.min(255, base + over * op);
const blendMultiply = (base, over, op) => base + ((base * over / 255) - base) * op;
const blendSoftLight = (base, over, op) => {
  const b = base / 255, o = over / 255;
  const result = o < 0.5
    ? b - (1 - 2 * o) * b * (1 - b)
    : b + (2 * o - 1) * (Math.sqrt(b) - b);
  return Math.min(255, Math.max(0, (b + (result - b) * op) * 255));
};
const blendOverlay = (base, over, op) => {
  const b = base / 255, o = over / 255;
  const result = b <= 0.5 ? 2 * b * o : 1 - 2 * (1 - b) * (1 - o);
  return Math.min(255, Math.max(0, (b + (result - b) * op) * 255));
};
const blendScreen = (base, over, op) => {
  const result = 1 - (1 - base / 255) * (1 - over / 255);
  return Math.min(255, Math.max(0, base + (result * 255 - base) * op));
};

const BLEND_FN_MAP = {
  multiply: blendMultiply,
  softLight: blendSoftLight,
  overlay: blendOverlay,
  screen: blendScreen,
  linearDodge: blendLinearDodge,
};

/**
 * Apply a blend mode between base and overlay pixel arrays.
 * @param {Uint8ClampedArray} basePixels - modified in place
 * @param {Uint8ClampedArray} overlayPixels
 * @param {object} opts - { hue, opacity, blendMode }
 */
export function applyBlend(basePixels, overlayPixels, opts) {
  // Backwards compat: support old 6-arg signature
  let hue, opacity, blendMode;
  if (typeof opts === 'object' && !Array.isArray(opts)) {
    ({ hue, opacity, blendMode } = opts);
  } else {
    // Old call: applyBlend(base, overlay, len, hue, opacity, blendMode)
    hue = arguments[3]; opacity = arguments[4]; blendMode = arguments[5];
  }
  const len = basePixels.length;
  const opVal = opacity / 100;
  const doHue = hue !== 0;
  const fn = BLEND_FN_MAP[blendMode] || blendLinearDodge;

  for (let i = 0; i < len; i += 4) {
    const oA = overlayPixels[i + 3];
    if (oA === 0) continue;
    let oR = overlayPixels[i], oG = overlayPixels[i + 1], oB = overlayPixels[i + 2];
    if (doHue) {
      const [h, s, l] = rgbToHsl(oR, oG, oB);
      const nh = ((h + hue) % 360 + 360) % 360;
      [oR, oG, oB] = hslToRgb(nh, s, l);
    }
    const effOp = opVal * (oA / 255);
    basePixels[i] = fn(basePixels[i], oR, effOp);
    basePixels[i + 1] = fn(basePixels[i + 1], oG, effOp);
    basePixels[i + 2] = fn(basePixels[i + 2], oB, effOp);
  }
}

/* ---- Adjustment sub-functions ----
   Each handles a single concern. Combined in applyAdjustments. */

function applyEBC(pixels, len, exposure, brightness, contrast) {
  const expF = Math.pow(2, exposure / 50);
  const brightAdj = brightness * 2.55;
  const contF = (259 * (contrast + 255)) / (255 * (259 - contrast));
  for (let i = 0; i < len; i += 4) {
    pixels[i]     = contF * ((pixels[i] * expF + brightAdj) - 128) + 128;
    pixels[i + 1] = contF * ((pixels[i + 1] * expF + brightAdj) - 128) + 128;
    pixels[i + 2] = contF * ((pixels[i + 2] * expF + brightAdj) - 128) + 128;
  }
}

function applyWarmthTint(pixels, len, warmth, tint) {
  const warmH = warmth * 0.5;
  const tintH = tint * 0.5;
  for (let i = 0; i < len; i += 4) {
    pixels[i] += warmH; pixels[i + 2] -= warmH; pixels[i + 1] += tintH;
  }
}

function clampPixels(pixels, len) {
  for (let i = 0; i < len; i += 4) {
    const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
    if (r < 0) pixels[i] = 0; else if (r > 255) pixels[i] = 255;
    if (g < 0) pixels[i + 1] = 0; else if (g > 255) pixels[i + 1] = 255;
    if (b < 0) pixels[i + 2] = 0; else if (b > 255) pixels[i + 2] = 255;
  }
}

function applySaturationVibrance(pixels, len, saturation, vibrance) {
  const satF = saturation / 100;
  const vibF = vibrance / 100;
  for (let i = 0; i < len; i += 4) {
    const [h, s, l] = rgbToHsl(pixels[i], pixels[i + 1], pixels[i + 2]);
    const ns = Math.max(0, Math.min(1, s + satF + (1 - s) * vibF));
    const [r, g, b] = hslToRgb(h, ns, l);
    pixels[i] = r; pixels[i + 1] = g; pixels[i + 2] = b;
  }
}

function applyDefinition(pixels, len, definition) {
  const defF = 1 + definition / 200;
  for (let i = 0; i < len; i += 4) {
    const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
    pixels[i]     = gray + (pixels[i] - gray) * defF;
    pixels[i + 1] = gray + (pixels[i + 1] - gray) * defF;
    pixels[i + 2] = gray + (pixels[i + 2] - gray) * defF;
  }
}

function applySharpness(pixels, width, height, sharpness) {
  const strength = sharpness / 100;
  const src = new Uint8ClampedArray(pixels);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) {
        const center = src[i + c];
        const detail = center * 4
          - src[i + c - 4]
          - src[i + c + 4]
          - src[i + c - width * 4]
          - src[i + c + width * 4];
        const v = center + detail * strength;
        pixels[i + c] = v < 0 ? 0 : v > 255 ? 255 : v;
      }
    }
  }
}

function applyVignette(pixels, width, height, vignette) {
  const cx = width / 2, cy = height / 2;
  const maxDist = Math.sqrt(cx * cx + cy * cy);
  const isNeg = vignette < 0;
  const str = Math.abs(vignette) / 100;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const dist = Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy)) / maxDist;
      const amount = dist * dist * str;
      if (isNeg) {
        pixels[i]     = Math.min(255, pixels[i] + (255 - pixels[i]) * amount);
        pixels[i + 1] = Math.min(255, pixels[i + 1] + (255 - pixels[i + 1]) * amount);
        pixels[i + 2] = Math.min(255, pixels[i + 2] + (255 - pixels[i + 2]) * amount);
      } else {
        const vf = 1 - amount;
        pixels[i] *= vf; pixels[i + 1] *= vf; pixels[i + 2] *= vf;
      }
    }
  }
}

function applyPixelation(ctx, width, height, pixelation) {
  const bs = Math.max(2, pixelation);
  const imgData = ctx.getImageData(0, 0, width, height);
  const d = imgData.data;
  for (let by = 0; by < height; by += bs) {
    for (let bx = 0; bx < width; bx += bs) {
      let r = 0, g = 0, b = 0, count = 0;
      const maxY = Math.min(by + bs, height);
      const maxX = Math.min(bx + bs, width);
      for (let py = by; py < maxY; py++) {
        for (let px = bx; px < maxX; px++) {
          const idx = (py * width + px) * 4;
          r += d[idx]; g += d[idx + 1]; b += d[idx + 2]; count++;
        }
      }
      r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
      for (let py = by; py < maxY; py++) {
        for (let px = bx; px < maxX; px++) {
          const idx = (py * width + px) * 4;
          d[idx] = r; d[idx + 1] = g; d[idx + 2] = b;
        }
      }
    }
  }
  ctx.putImageData(imgData, 0, 0);
}

/**
 * Apply all image adjustments. Returns true if ctx already has the final image.
 * @param {Uint8ClampedArray} pixels
 * @param {number} width
 * @param {number} height
 * @param {object} adj - adjustment values
 * @param {CanvasRenderingContext2D} [ctx] - optional, needed for blur/pixelation
 */
export function applyAdjustments(pixels, width, height, adj, ctx) {
  const len = pixels.length;

  // Pass 1: per-pixel color math (combined when possible for cache efficiency)
  if (adj.exposure !== 0 || adj.brightness !== 0 || adj.contrast !== 0) {
    applyEBC(pixels, len, adj.exposure, adj.brightness, adj.contrast);
  }
  if (adj.warmth !== 0 || adj.tint !== 0) {
    applyWarmthTint(pixels, len, adj.warmth, adj.tint);
  }
  // Clamp before HSL conversions
  clampPixels(pixels, len);

  if (adj.saturation !== 0 || adj.vibrance !== 0) {
    applySaturationVibrance(pixels, len, adj.saturation, adj.vibrance);
  }
  if (adj.definition !== 0) {
    applyDefinition(pixels, len, adj.definition);
    clampPixels(pixels, len);
  }

  // Pass 2: spatial effects (need neighbor access)
  if (adj.sharpness !== 0) applySharpness(pixels, width, height, adj.sharpness);
  if (adj.vignette !== 0) applyVignette(pixels, width, height, adj.vignette);

  // Pass 3: canvas-level effects (GPU-accelerated blur)
  if (ctx && (adj.pixelation > 0 || adj.blur > 0)) {
    ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
    if (adj.blur > 0) {
      ctx.filter = `blur(${adj.blur * 0.2}px)`;
      ctx.drawImage(ctx.canvas, 0, 0);
      ctx.filter = 'none';
    }
    if (adj.pixelation > 0) applyPixelation(ctx, width, height, adj.pixelation);
    return true;
  }
  return false;
}

/* ---- Pixelation effect (collage regions) ---- */
export function pixelateRegion(ctx, x, y, w, h, blockSize) {
  if (w <= 0 || h <= 0) return;
  const bs = Math.max(4, blockSize);
  const imgData = ctx.getImageData(x, y, w, h);
  const d = imgData.data;
  for (let by = 0; by < h; by += bs) {
    for (let bx = 0; bx < w; bx += bs) {
      let r = 0, g = 0, b = 0, count = 0;
      const maxY = Math.min(by + bs, h);
      const maxX = Math.min(bx + bs, w);
      for (let py = by; py < maxY; py++) {
        for (let px = bx; px < maxX; px++) {
          const i = (py * w + px) * 4;
          r += d[i]; g += d[i + 1]; b += d[i + 2]; count++;
        }
      }
      r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
      for (let py = by; py < maxY; py++) {
        for (let px = bx; px < maxX; px++) {
          const i = (py * w + px) * 4;
          d[i] = r; d[i + 1] = g; d[i + 2] = b;
        }
      }
    }
  }
  ctx.putImageData(imgData, x, y);
}

/* ---- Dimension helpers ---- */

export function getPreviewDims(w, h) {
  const pixels = w * h;
  if (pixels <= 150000) return { width: w, height: h };
  const scale = Math.sqrt(150000 / pixels);
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

export function getMobilePreviewDims(w, h) {
  const pixels = w * h;
  if (pixels <= 60000) return { width: w, height: h };
  const scale = Math.sqrt(60000 / pixels);
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

export function getExportDims(w, h, isMobile, maxExportPx) {
  if (!isMobile) return { width: w, height: h };
  const longest = Math.max(w, h);
  if (longest <= maxExportPx) return { width: w, height: h };
  const scale = maxExportPx / longest;
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}
